import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiFilter,
  FiPlay,
  FiBookOpen,
  FiRefreshCw,
  FiGrid,
  FiList
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/common/Badge';
import { useNotification } from '../../contexts/NotificationContext';
import { testAPI } from '../../services/api';

const PracticeTests = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // State management
  const [practiceOptions, setPracticeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    subject: '',
    examType: '',
    sortBy: 'subject',
    sortOrder: 'asc'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [generatingTest, setGeneratingTest] = useState(null);

  // Fetch available practice options from question bank
  const fetchPracticeOptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await testAPI.getPracticeOptions();
      
      if (response.data.success) {
        // Extract the practiceOptions array from the response data
        setPracticeOptions(response.data.data.practiceOptions || []);
      } else {
        throw new Error(response.data.message || 'Failed to fetch practice options');
      }
    } catch (err) {
      console.error('Error fetching practice options:', err);
      setError(err.message || 'Failed to load practice options');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchPracticeOptions();
  }, [fetchPracticeOptions]);

  // Filter and search practice options
  const filteredOptions = (Array.isArray(practiceOptions) ? practiceOptions : []).filter(option => {
    const matchesSearch = !searchQuery || 
      option.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      option.examType.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSubject = !filters.subject || option.subject === filters.subject;
    const matchesExamType = !filters.examType || option.examType === filters.examType;
    
    return matchesSearch && matchesSubject && matchesExamType;
  }).sort((a, b) => {
    let aValue, bValue;
    
    if (filters.sortBy === 'subject') {
      aValue = a.subject;
      bValue = b.subject;
    } else if (filters.sortBy === 'examType') {
      aValue = a.examType;
      bValue = b.examType;
    } else if (filters.sortBy === 'questionCount') {
      aValue = a.questionCount;
      bValue = b.questionCount;
    }
    
    if (filters.sortOrder === 'desc') {
      return bValue > aValue ? 1 : -1;
    }
    return aValue > bValue ? 1 : -1;
  });

  // Get unique subjects and exam types for filters
  const uniqueSubjects = [...new Set((Array.isArray(practiceOptions) ? practiceOptions : []).map(option => option.subject))];
  const uniqueExamTypes = [...new Set((Array.isArray(practiceOptions) ? practiceOptions : []).map(option => option.examType))];

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle practice test generation and start (or resume if in progress)
  const handleStartPracticeTest = async (subject, examType, questionCount) => {
    try {
      setGeneratingTest(`${subject}-${examType}`);

      // Check for in-progress session (backend) so we can resume instead of starting new
      try {
        const progressRes = await testAPI.getPracticeProgress({ subject, examType });
        const progress = progressRes.data?.data;
        if (progress?.isStarted && progress?.questionData?.length) {
          navigate('/practice-test/take', {
            state: {
              savedProgress: true,
              progressData: progress
            }
          });
          setGeneratingTest(null);
          return;
        }
      } catch (_) {}

      const response = await testAPI.generatePracticeTest({
        subject,
        examType,
        questionCount: Math.min(questionCount, 50)
      });

      if (response.data.success) {
        const testData = response.data.data.test;
        navigate('/practice-test/take', {
          state: {
            testData,
            subject,
            examType,
            isDynamic: true
          }
        });
      } else {
        throw new Error(response.data.message || 'Failed to generate practice test');
      }
    } catch (err) {
      console.error('Error generating practice test:', err);
      showNotification(err.message || 'Failed to generate practice test', 'error');
    } finally {
      setGeneratingTest(null);
    }
  };

  // Get exam type color
  const getExamTypeColor = (examType) => {
    switch (examType?.toLowerCase()) {
      case 'jamb': return 'primary';
      case 'waec': return 'success';
      case 'neco': return 'warning';
      case 'gce': return 'info';
      default: return 'secondary';
    }
  };

  const renderPracticeCard = (option) => {
    const isGenerating = generatingTest === `${option.subject}-${option.examType}`;
    const cardContent = (
      <>
        <div className="flex justify-between items-start gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900">{option.subject} Practice</h3>
            <p className="text-sm text-slate-500 mt-0.5">Practice {option.subject} for {option.examType}</p>
          </div>
          <Badge variant={getExamTypeColor(option.examType)} size="small">{option.examType}</Badge>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-3">
          <span>{option.questionCount} questions</span>
          <span>Dynamic duration</span>
          <span>Practice mode</span>
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={() => handleStartPracticeTest(option.subject, option.examType, option.questionCount)}
            disabled={isGenerating}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <><LoadingSpinner size="sm" /> Generating...</>
            ) : (
              <><FiPlay className="h-4 w-4" /> Start practice</>
            )}
          </button>
        </div>
      </>
    );
    if (viewMode === 'list') {
      return (
        <div key={`${option.subject}-${option.examType}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 hover:bg-slate-50/50 transition-colors">
          <div className="flex justify-between items-start gap-3 min-w-0">
            <div>
              <h3 className="font-semibold text-slate-900">{option.subject} Practice</h3>
              <p className="text-sm text-slate-500 mt-0.5">{option.examType} · {option.questionCount} questions</p>
            </div>
            <Badge variant={getExamTypeColor(option.examType)} size="small">{option.examType}</Badge>
          </div>
          <button
            type="button"
            onClick={() => handleStartPracticeTest(option.subject, option.examType, option.questionCount)}
            disabled={isGenerating}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-60 flex-shrink-0"
          >
            {isGenerating ? <LoadingSpinner size="sm" /> : <FiPlay className="h-4 w-4" />} Start practice
          </button>
        </div>
      );
    }
    return (
      <div key={`${option.subject}-${option.examType}`} className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 flex flex-col">
        {cardContent}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-sm text-slate-500">Loading practice options...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <div className="text-center max-w-sm px-4">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Unable to load practice tests</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <button type="button" onClick={fetchPracticeOptions} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800">
            <FiRefreshCw className="h-4 w-4" /> Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header - match Dashboard */}
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900">Practice tests</h1>
          <p className="text-sm text-slate-500 mt-0.5">Practice with questions from the question bank.</p>
        </div>

        {/* Search and filters in a white card */}
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <form onSubmit={handleSearch} className="flex-1 min-w-0 max-w-md">
              <label className="sr-only">Search</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by subject or exam type..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 bg-white text-slate-900"
                />
              </div>
            </form>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${showFilters ? 'bg-slate-100 border-slate-300 text-slate-900' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              >
                <FiFilter className="h-4 w-4" /> Filters
              </button>
              <div className="flex rounded-lg border border-slate-200 bg-slate-50/50 p-0.5">
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  title="Grid view"
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-md text-sm ${viewMode === 'grid' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <FiGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  title="List view"
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-md text-sm ${viewMode === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <FiList className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Subject</label>
                <select value={filters.subject} onChange={(e) => handleFilterChange('subject', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                  <option value="">All subjects</option>
                  {uniqueSubjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Exam type</label>
                <select value={filters.examType} onChange={(e) => handleFilterChange('examType', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                  <option value="">All types</option>
                  {uniqueExamTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Sort by</label>
                <select value={filters.sortBy} onChange={(e) => handleFilterChange('sortBy', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                  <option value="subject">Subject</option>
                  <option value="examType">Exam type</option>
                  <option value="questionCount">Questions</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Order</label>
                <select value={filters.sortOrder} onChange={(e) => handleFilterChange('sortOrder', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
              <div className="sm:col-span-2 lg:col-span-4 flex items-end">
                <button type="button" onClick={() => setFilters({ subject: '', examType: '', sortBy: 'subject', sortOrder: 'asc' })} className="text-sm font-medium text-slate-600 hover:text-slate-900">Clear filters</button>
              </div>
            </div>
          )}
        </div>

        {/* Practice options grid */}
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          {filteredOptions.length === 0 ? (
            <div className="py-16 px-6 text-center">
              <FiBookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No practice options</h3>
              <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
                {searchQuery || filters.subject || filters.examType ? 'No options match your filters.' : 'No questions in the question bank yet.'}
              </p>
              {(searchQuery || filters.subject || filters.examType) && (
                <button type="button" onClick={() => { setSearchQuery(''); setFilters({ subject: '', examType: '', sortBy: 'subject', sortOrder: 'asc' }); }} className="text-sm font-medium text-slate-600 hover:text-slate-900 underline">Clear filters</button>
              )}
            </div>
          ) : (
            <div className={viewMode === 'list' ? 'divide-y divide-slate-100' : 'p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'}>
              {filteredOptions.map(renderPracticeCard)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PracticeTests;